import { gql } from '@apollo/client';
import { MeetingPlacePageEventListItemFragment } from '../fragments/meetingPlacePageEventItem';
import { MeetingPlacePageEventListFragment } from '../fragments/meetingPlacePageEventList';

export const MEETING_PLACE_PAGE_EVENT_LIST_QUERY = gql`
  ${MeetingPlacePageEventListFragment}
  ${MeetingPlacePageEventListItemFragment}
  query event_listing_query(
    $id: String!
    $limit: Int!
    $skip: Int!
    $preview: Boolean
    $language: String!
  ) {
    meetingPlacePageEventList(id: $id, preview: $preview, locale: $language) {
      ...MeetingPlacePageEventList
      eventItemsCollection(
        limit: $limit
        skip: $skip
        preview: $preview
        locale: $language
      ) {
        total
        items {
          ...MeetingPlacePageEventListItem
        }
      }
    }
  }
`;

export const MEETING_PLACE_PAGE_EVENT_LIST_SEO_QUERY = gql`
  ${MeetingPlacePageEventListFragment}
  query event_listing_seo_query(
    $id: String!
    $preview: Boolean
    $locale: String!
  ) {
    meetingPlacePageEventList(id: $id, preview: $preview, locale: $locale) {
      ...MeetingPlacePageEventList
    }
  }
`;

// Helper function to fetch event list data with pagination (server-side)
async function fetchEventListData(
  client: any,
  id: string,
  limit: number = 3,
  preview: boolean,
  language: string,
) {
  let skip = 0;
  const allItems: any[] = [];
  let total = 0;
  let completeData: any = null;

  do {
    const res: any = await client.query({
      query: MEETING_PLACE_PAGE_EVENT_LIST_QUERY,
      variables: {
        id,
        limit,
        skip,
        preview,
        language,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    const eventList = res?.data?.meetingPlacePageEventList;
    if (!eventList) break;

    const collection = eventList.eventItemsCollection;
    if (!collection) break;

    if (!completeData) {
      completeData = {
        ...eventList,
        eventItemsCollection: {
          ...collection,
          items: [],
        },
      };
    }

    allItems.push(...(collection.items || []));
    total = collection.total || 0;
    skip += limit;
  } while (allItems.length < total && skip < total);

  if (completeData) {
    completeData.eventItemsCollection = {
      ...completeData.eventItemsCollection,
      items: allItems,
      total,
    };
  }

  return completeData;
}

// Helper function to fetch occasion list data with pagination (server-side)
// async function fetchOccasionListData(
//   client: any,
//   id: string,
//   limit: number = 3,
//   preview: boolean,
//   language: string,
// ) {
//   const { MEETING_PLACE_PAGE_Occasion_LIST_QUERY } =
//     await import('./meetingPlacePageOccasionListQuery');

//   let skip = 0;
//   const allItems: any[] = [];
//   let total = 0;
//   let completeData: any = null;

//   do {
//     const res: any = await client.query({
//       query: MEETING_PLACE_PAGE_Occasion_LIST_QUERY,
//       variables: {
//         id,
//         limit,
//         skip,
//         preview,
//         locale: language,
//       },
//       fetchPolicy: 'no-cache',
//       errorPolicy: 'all',
//     });

//     const occasions = res?.data?.meetingPlacePageOccasions;
//     if (!occasions) break;

//     const collection = occasions.occasionsCollection;
//     if (!collection) break;

//     if (!completeData) {
//       completeData = {
//         ...occasions,
//         occasionsCollection: {
//           ...collection,
//           items: [],
//         },
//       };
//     }

//     allItems.push(...(collection.items || []));
//     total = collection.total || 0;
//     skip += limit;
//   } while (allItems.length < total && skip < total);

//   if (completeData) {
//     completeData.occasionsCollection = {
//       ...completeData.occasionsCollection,
//       items: allItems,
//       total,
//     };
//   }

//   return completeData;
// }
export async function fetchOccasionListData(
  client: any,
  id: string,
  limit: number = 3,
  preview: boolean,
  language: string,
) {
  const {
    MEETING_PLACE_PAGE_OFFERS_QUERY,
    MEETING_PLACE_PAGE_EVENTS_NEWS_QUERY,
  } = await import('./meetingPlacePageOccasionListQuery');

  // Generic paginator — works for either query
  async function fetchAllItems(
    query: any,
  ): Promise<{ items: any[]; total: number; baseData: any }> {
    let skip = 0;
    const allItems: any[] = [];
    let total = 0;
    let baseData: any = null;

    do {
      const res: any = await client.query({
        query,
        variables: { id, limit, skip, preview, locale: language },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });

      const occasions = res?.data?.meetingPlacePageOccasions;
      if (!occasions) break;

      const collection = occasions.occasionsCollection;
      if (!collection) break;

      // Capture top-level occasion fields once (from first page)
      if (!baseData) {
        baseData = {
          ...occasions,
          occasionsCollection: { ...collection, items: [] },
        };
      }

      allItems.push(...(collection.items || []));
      total = collection.total || 0;
      skip += limit;
    } while (allItems.length < total && skip < total);

    return { items: allItems, total, baseData };
  }

  // Run both queries in parallel
  const [offersResult, eventsNewsResult] = await Promise.all([
    fetchAllItems(MEETING_PLACE_PAGE_OFFERS_QUERY),
    fetchAllItems(MEETING_PLACE_PAGE_EVENTS_NEWS_QUERY),
  ]);

  // Use baseData from either result (both carry the same top-level occasion fields)
  const baseData = offersResult.baseData ?? eventsNewsResult.baseData;
  if (!baseData) return null;

  // Merge items from both queries and sum totals
  const mergedItems = [...offersResult.items, ...eventsNewsResult.items];
  const mergedTotal = offersResult.total + eventsNewsResult.total;

  return {
    ...baseData,
    occasionsCollection: {
      ...baseData.occasionsCollection,
      items: mergedItems,
      total: mergedTotal,
    },
  };
}
// Helper function to fetch event list page data server-side
export async function getMeetingPlacePageEventListData(
  eventListId: string,
  occasionId: string | null,
  tagId: string,
  language: string,
  preview: boolean,
) {
  const { default: client } = await import('../apolloClient');
  const { MEETING_PLACE_PAGE_HOME_EXTENDED_LOGO_QUERY } =
    await import('./meetingPlacePageEditorialListQuery');

  // Fetch all data in parallel
  const [eventListData, homeData, occasionData] = await Promise.all([
    fetchEventListData(client, eventListId, 100, preview, language),
    client.query({
      query: MEETING_PLACE_PAGE_HOME_EXTENDED_LOGO_QUERY,
      variables: { tagId, language },
      fetchPolicy: 'no-cache',
    }),
    occasionId
      ? fetchOccasionListData(client, occasionId, 40, preview, language)
      : Promise.resolve(null),
  ]);

  const homePageData: any =
    (homeData?.data as any)?.meetingPlacePageHomeCollection?.items?.[0] || null;

  // const slotsData = await getEventSlotsData({
  //   mpId: homePageData?.meetingPlace?.businessUnitCode,
  // });

  return {
    meetingPlacePageEventList: eventListData,
    meetingPlacePageOccasions: occasionData,
    meetingPlacePageHomeCollection: homePageData,
    eventsSlotsData: [],
  };
}

